/**
 * Relix — centralized brand configuration.
 *
 * Single source of truth for the product name, taglines, CTAs, email
 * addresses, and category labels. Import from here instead of hardcoding
 * the brand name in JSX or copy strings.
 */

/** Set true when a public in-app product demo route exists (e.g. `/demo`). */
export const HAS_PRODUCT_DEMO_ROUTE = false;

export const brand = {
  name: 'Relix',
  previousName: 'Workalytix',
  previousNames: ['Workalytix', 'Svodion', 'SvodX'] as const,

  tagline: 'Know what will ship before it slips.',
  shortTagline: 'AI Delivery Intelligence for Jira Teams',

  category: 'AI Delivery Intelligence for Jira Teams',
  currentWedge: 'Your AI Product Owner for Jira Delivery',

  /** Hero — lead with the emotional outcome, support with the positioning. */
  heroEyebrow: 'AI DELIVERY INTELLIGENCE',
  heroBrandLine: 'Relix',
  heroMainHeadline: 'Know what will ship before it slips.',
  heroHeadline: 'Know what will ship before it slips.',
  heroSubhead: 'Your AI Product Owner for Jira Delivery',
  heroSupportLine:
    'Relix connects capacity, forecasts, releases, calendars, and commitments into one delivery command center — so product leaders can see what changed, what is at risk, and what can actually ship.',

  founderBadge: 'Built from real Product Owner workflows',

  /** Capabilities grid (Solutions landing). */
  capabilitySectionTitle: 'One command center for Jira delivery',
  capabilitySectionSubtitle:
    'From capacity to commitments, Relix turns Jira signals into clarity and confidence.',

  solutionFeatures: [
    {
      title: 'Capacity Truth',
      description:
        'Reconcile logged work, calendars, and Jira assignments so capacity reflects reality.',
    },
    {
      title: 'Release Risk',
      description:
        'Surface scope creep, slipping issues, and dependency risk before milestones slip.',
    },
    {
      title: 'Forecast Reality',
      description: 'Ground ship dates in velocity and history—not wishful timelines.',
    },
    {
      title: 'Commitment Review',
      description: 'Compare what was promised to what shipped, without spreadsheets.',
    },
    {
      title: 'Quality Signals',
      description: 'Spot bug hotspots, reopens, and quality drag before it compounds.',
    },
    {
      title: 'Executive Reports',
      description: 'Turn delivery signals into stakeholder-ready summaries and exports.',
    },
  ] as const,

  metricCards: [
    {
      kicker: '',
      title: 'Capacity Truth',
      description: 'See real team capacity before dates are promised.',
    },
    {
      kicker: '',
      title: 'Release Risk',
      description: 'Catch slippage before the milestone conversation.',
    },
    {
      kicker: '',
      title: 'Plan vs Delivery',
      description: 'Compare promised scope with what actually shipped.',
    },
  ] as const,

  /** Static pricing card (no Stripe slug) — inserted between Team and Enterprise. */
  pricingOrganizationCard: {
    name: 'Organization',
    tagline: 'For multiple products, teams, releases, and stakeholders.',
    features: [
      'Multi-product Jira workspaces',
      'Release and forecast views for PMs and leadership',
      'Commitment and delivery review across teams',
      'Calendar and absence signals where configured',
      'Azure AD / SSO where configured',
      'Priority support',
    ] as const,
    ctaLabel: 'Book Demo',
  },

  ctaClosingHeadline: 'Executive clarity on what your team can actually ship.',
  ctaClosingSub:
    'Request a founder-led delivery audit console session: we review your real Jira workflow, quantify capacity and risk, and return practical recommendations — no sandbox, no generic playbook.',

  ctaEmailPlaceholder: 'Work email',
  ctaCompanyPlaceholder: 'Company / Team name',
  ctaMessagePlaceholder: 'Biggest delivery problem',
  ctaSubmitLabel: 'Get a Free Delivery Audit',

  talkToFounderCta: 'Talk to Founder',

  pricingHeroTitle: 'Start with a Delivery Audit. Scale when the value is clear.',
  pricingHeroSub:
    'AI Delivery Intelligence for Jira Teams — understand real capacity, release risk, commitments, and delivery patterns before rolling out across teams.',

  salesEmail: 'kirill@relix.delivery',
  supportEmail: 'kirill@relix.delivery',
  founderDiscussEmail: 'kirill@relix.delivery',

  primaryCta: 'Get a Free Delivery Audit',
  secondaryCta: 'Book a Guided Walkthrough',
  pilotCta: 'Apply for Pilot',

  authVisualH2: 'Your delivery command center',
  authVisualLead:
    'After you connect Jira, Relix helps you see what will ship, what is drifting, and what needs attention.',
  authVisualBullets: [
    'Know what will ship before it slips.',
    'Spot release risk before the last mile.',
    'Brief leadership from Jira — without rebuilding decks.',
  ] as const,
  authVisualTagline: 'PLAN. SIGNAL. DELIVER.',

  /** Current storage prefix; migration handles the svodion -> relix transition. */
  storagePrefix: 'relix',
  previousStoragePrefix: 'svodion',
  previousStoragePrefix2: 'workalytix',

  /** Current event prefix; old svodion: events still dispatched as compat layer. */
  eventPrefix: 'relix',
  previousEventPrefix: 'svodion',
} as const;
